import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../../services/auth.service';
import { AdministrationService } from '../../../services/administration.service';
import { Credencial } from '../../../models/User';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScnackbarData, ToastService, SnackbarStyles } from '../../../commons/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AppTitleService } from '../../../services/app-title.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { DlgAlertComponent } from '../../../commons/dlg-alert/dlg-alert.component';

export function passwordMatchValidator(newPasswordKey: string, confirmPasswordKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const newPassword = control.get(newPasswordKey)?.value;
    const confirmPassword = control.get(confirmPasswordKey)?.value;
    if (newPassword === "" || confirmPassword == "") {
      return null
    }
    if (newPassword !== confirmPassword) {
      control.get(confirmPasswordKey)?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      return null;
    }
  };
}

// Validador para longitud mínima
export function minLengthValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return value.length >= minLength ? null : { minLength: { requiredLength: minLength, actualLength: value.length } };
  };
}

// Validador para mayúsculas
export function uppercaseValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[A-Z]/.test(value) ? null : { uppercase: true };
  };
}

// Validador para minúsculas
export function lowercaseValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[a-z]/.test(value) ? null : { lowercase: true };
  };
}

// Validador para números
export function numberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[0-9]/.test(value) ? null : { number: true };
  };
}

// Validador para caracteres especiales
export function specialCharValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[!@#$%^&*(),.?":{}|<>]/.test(value) ? null : { specialChar: true };
  };
}

// Validador para que la nueva contraseña no sea igual a la actual
export function passwordNotSameValidator(currentPasswordKey: string, newPasswordKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const currentPassword = control.get(currentPasswordKey)?.value;
    const newPassword = control.get(newPasswordKey)?.value;
    if (currentPassword === "" || newPassword === "") {
      return null;
    }
    if (currentPassword === newPassword) {
      control.get(newPasswordKey)?.setErrors({ samePassword: true });
      return { samePassword: true };
    } else {
      return null;
    }
  };
}

@Component({
    selector: 'app-cambio-contrasena',
    imports: [
        ReactiveFormsModule, MatToolbarModule,
        MatFormFieldModule,
        MatInputModule, MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './cambio-contrasena.component.html',
    styleUrl: './cambio-contrasena.component.scss',
    changeDetection: ChangeDetectionStrategy.Default
})
export class CambioContrasenaComponent {
  hideActual = signal(true);
  hideNueva = signal(true);
  hideConfirmar = signal(true);

  fetchingUpdatePassword = false
  submitted = false
  incorrectPasswordError = false

  snackbarData: ScnackbarData = {}

  updatePasswordFormControl = new FormGroup({
    currentPassword: new FormControl("", [
      Validators.required
    ]),
    newPassword: new FormControl("", [
      Validators.required,
      minLengthValidator(10),
      uppercaseValidator(),
      lowercaseValidator(),
      numberValidator(),
      specialCharValidator()
    ]),
    confirmPassword: new FormControl("", [
      Validators.required,
    ])
  }, {
    validators: [
      passwordMatchValidator('newPassword', 'confirmPassword')
    ]
  })
  readonly dialog = inject(MatDialog);
  @ViewChild('currentPasswordInput') currentPasswordInput!: ElementRef<HTMLInputElement>;

  constructor(    
    private authService: AuthService,
    private router: Router,    
    private administrationService: AdministrationService,
    private toast: ToastService
  ) {
    inject(AppTitleService).setTitle("Administraci\xf3n");
  }

  toggleVisibility(signalVar: any, event: MouseEvent) {
    signalVar.set(!signalVar());
    event.stopPropagation();
  }

  onSubmitPasswordForm() {
    this.submitted = true;
    this.incorrectPasswordError = false;
    
    // Validar que la nueva contraseña no sea igual a la actual
    const currentPassword = this.updatePasswordFormControl.controls.currentPassword.value;
    const newPassword = this.updatePasswordFormControl.controls.newPassword.value;
    
    if (currentPassword === newPassword) {
      this.snackbarData.message = "La nueva contraseña no puede ser igual a la actual"
      this.snackbarData.style = SnackbarStyles.Error
      this.toast.show(this.snackbarData)
      return;
    }
    
    this.fetchingUpdatePassword = true
    const userData = this.authService.getTokenData()
    let credencial: Credencial = {
      id: parseInt(userData!.id),
      usuario: userData!.sub,
      contrasena: this.updatePasswordFormControl.controls.currentPassword.value!,
      nuevaContrasena: this.updatePasswordFormControl.controls.newPassword.value!
    }
    this.administrationService.PutCurrentUserPassword(credencial).subscribe({
      next: (response) => {
        this.fetchingUpdatePassword = false
        credencial = response        
        this.snackbarData.message = "Contrase\xf1a actualizada!"
        this.snackbarData.style = SnackbarStyles.Success
        this.toast.show(this.snackbarData)   
        this.dialog.open(DlgAlertComponent, {
          data: {
            title: 'Cambio de contrase\xf1a',
            message: 'Por favor, ingrese nuevamente al sistema para validar su nueva contrase\xf1a.',
            type: 'info'
          }
        }).afterClosed().subscribe(() => {
          this.authService.logout()
        });
      },
      error: (error: HttpErrorResponse) => {
        let errorMessage = "Error al actualizar la contraseña";
        
        if (error.status === 406) {
          const errorBody = error.error;
          if (errorBody === 'old-mismatch') {
            errorMessage = "La contraseña actual es incorrecta";
            this.incorrectPasswordError = true;
          } else if (errorBody === 'same-password') {
            errorMessage = "La nueva contraseña no puede ser igual a la actual";
          } else if (errorBody === 'weak-password') {
            errorMessage = "La contraseña es muy débil. Debe ser más segura";
          } else {
            errorMessage = "Revisar los datos!";
          }
        } else {
          errorMessage = "Error actualizando!";
        }
        
        this.snackbarData.message = errorMessage
        this.snackbarData.style = SnackbarStyles.Error
        this.fetchingUpdatePassword = false
        this.toast.show(this.snackbarData)
        this.currentPasswordInput.nativeElement.focus();
      }
    })
  }

  onBack(){
    this.router.navigate(["/administracion"]);
  }

}
